import { Artefact, ArtefactType, ArtefactDetectionConfig } from "@/types/artefacts";

import { BaseArtefactDetector } from "./BaseDetector";

/**
 * Generate a human-readable title from column headers
 * Creates concise descriptions like "Company Details", "Name, Age, Location"
 */
function generateTitleFromHeaders(headers: string[]): string {
  if (!headers || headers.length === 0) {
    return "Table";
  }

  // Clean headers - remove markdown links and extra whitespace
  const cleanHeaders = headers.map((h) =>
    h
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Strip markdown links
      .replace(/\*+/g, "") // Strip bold/italic markers
      .trim(),
  );

  // If 1-3 columns, show all header names
  if (cleanHeaders.length <= 3) {
    return cleanHeaders.filter((h) => h.length > 0).join(", ");
  }

  // For more columns, show first 2 and indicate more
  const firstTwo = cleanHeaders.slice(0, 2).filter((h) => h.length > 0);

  return `${firstTwo.join(", ")} + ${cleanHeaders.length - 2} more`;
}

/**
 * Extract headers from different table formats
 */
function extractHeaders(content: string, format: "json" | "tsv" | "markdown"): string[] {
  try {
    if (format === "json") {
      const parsed = JSON.parse(content);

      if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === "object") {
        return Object.keys(parsed[0]);
      }
    } else if (format === "tsv") {
      const firstLine = content.split("\n")[0];

      return firstLine.split("\t").map((h) => h.trim());
    } else if (format === "markdown") {
      const firstLine = content.split("\n")[0];

      return firstLine
        .split("|")
        .map((h) => h.trim())
        .filter((h) => h.length > 0);
    }
  } catch {
    // Fallback on parse error
  }

  return [];
}

/** Row-count wording shared by the TSV and markdown scans. */
function describeRowCount(dataRowCount: number): string {
  return `${dataRowCount} ${dataRowCount === 1 ? "row" : "rows"}`;
}

/** A markdown table's separator row, e.g. `| --- | :---: |`. */
const MARKDOWN_SEPARATOR_ROW = /^\s*\|?[-|:\s]+\|?\s*$/;

const OPENER_FOR: Record<string, string> = { "]": "[", "}": "{" };

/** An opening bracket or brace still waiting for its closer. */
interface OpenBracket {
  char: string;
  index: number;
  /** True for a `[` whose first element is an object, i.e. a table candidate. */
  isCandidate: boolean;
}

/**
 * Single-pass scanner behind `findJsonArrayCandidates`. Each character is
 * read exactly once, and the state is split across small methods so every
 * path through the scan can be tested on its own.
 */
class JsonArrayScanner {
  private readonly spans: Array<{ start: number; end: number }> = [];
  private readonly stack: OpenBracket[] = [];
  private inString = false;
  private escaped = false;

  constructor(private readonly content: string) {}

  scan(): string[] {
    for (let i = 0; i < this.content.length; i++) {
      this.read(this.content[i], i);
    }

    return this.spans.map(({ start, end }) => this.content.slice(start, end));
  }

  private read(char: string, index: number): void {
    if (this.stack.length === 0) {
      this.lookForCandidate(char, index);
    } else if (this.inString) {
      this.readStringChar(char);
    } else {
      this.readStructuralChar(char, index);
    }
  }

  /** Outside any candidate, only a `[` that opens an object matters. */
  private lookForCandidate(char: string, index: number): void {
    if (char === "[" && this.opensObjectNext(index)) {
      this.stack.push({ char, index, isCandidate: true });
    }
  }

  private readStringChar(char: string): void {
    if (this.escaped) {
      this.escaped = false;
    } else if (char === "\\") {
      this.escaped = true;
    } else if (char === '"') {
      this.inString = false;
    } else if (char === "\n") {
      // A JSON string cannot hold a raw newline, so this was never JSON.
      this.abandon();
    }
  }

  private readStructuralChar(char: string, index: number): void {
    if (char === '"') {
      this.inString = true;
    } else if (char === "[" || char === "{") {
      this.stack.push({ char, index, isCandidate: char === "[" && this.opensObjectNext(index) });
    } else if (char in OPENER_FOR) {
      this.close(char, index);
    }
  }

  private close(char: string, index: number): void {
    const top = this.stack.pop();

    if (top?.char !== OPENER_FOR[char]) {
      this.abandon();
    } else if (top.isCandidate) {
      this.recordSpan(top.index, index + 1);
    }
  }

  /** A span that closes around earlier spans replaces them. */
  private recordSpan(start: number, end: number): void {
    while (this.spans.length > 0 && this.spans[this.spans.length - 1].start > start) {
      this.spans.pop();
    }
    this.spans.push({ start, end });
  }

  /** Drop the open candidate without rescanning any of it. */
  private abandon(): void {
    this.stack.length = 0;
    this.inString = false;
    this.escaped = false;
  }

  /** True when the first non-whitespace character after `index` is `{`. */
  private opensObjectNext(index: number): boolean {
    let i = index + 1;

    while (i < this.content.length && /\s/.test(this.content[i])) i++;

    return this.content[i] === "{";
  }
}

/**
 * Find every outermost `[ {` ... `]` span that could be a JSON array of
 * objects, in a single linear pass.
 *
 * This replaces a nested lazy regex that CodeQL flagged as a ReDoS risk
 * (`js/redos`), since its backtracking was polynomial in the length of
 * model output. Brackets and braces are matched with a stack, and string
 * literals are honoured only once a candidate is open, so prose apostrophes
 * outside JSON cannot derail the scan. A JSON string cannot contain a raw
 * newline, so meeting one inside a string, or meeting a closer that does not
 * match, abandons the open candidate rather than rescanning from it. Spans
 * that closed inside an abandoned candidate are still reported, and a span
 * that closes around earlier ones replaces them, which matches what the
 * regex consumed.
 */
export function findJsonArrayCandidates(content: string): string[] {
  return new JsonArrayScanner(content).scan();
}

/** What the three scans accumulate as they walk one message. */
interface TableScan {
  messageId: string;
  artefacts: Artefact[];
  tableIndex: number;
  skippedCount: number;
  foundAny: boolean;
}

/**
 * Detects data tables (JSON arrays, CSV-like content)
 */
export class TableDetector extends BaseArtefactDetector {
  detect(content: string, messageId: string, _config: ArtefactDetectionConfig) {
    if (!this.validateContent(content)) {
      return this.createResult([], 0, ["Invalid content provided for table detection"]);
    }

    const scan: TableScan = {
      messageId,
      artefacts: [],
      tableIndex: 0,
      skippedCount: 0,
      foundAny: false,
    };
    const warnings: string[] = [];

    this.scanJsonArrays(content, scan);
    this.scanTsvTables(content, scan);
    this.scanMarkdownTables(content, scan);

    // Only count skips if we actually found something to process
    const finalSkipCount = scan.foundAny ? scan.skippedCount : 0;

    return this.createResult(scan.artefacts, finalSkipCount, warnings);
  }

  private addTable(
    scan: TableScan,
    content: string,
    headers: string[],
    metadata: Artefact["metadata"],
  ): void {
    scan.artefacts.push({
      id: `${scan.messageId}-table-${scan.tableIndex}`,
      type: ArtefactType.TABLE,
      title: generateTitleFromHeaders(headers),
      content,
      messageId: scan.messageId,
      timestamp: this.getCurrentTimestamp(),
      metadata,
    });
    scan.tableIndex++;
  }

  /** Detect JSON arrays that look like tables */
  private scanJsonArrays(content: string, scan: TableScan): void {
    for (const jsonContent of findJsonArrayCandidates(content)) {
      scan.foundAny = true;
      try {
        const parsed = JSON.parse(jsonContent);

        if (
          Array.isArray(parsed) &&
          parsed.length > 1 &&
          parsed.every((item) => typeof item === "object" && item !== null)
        ) {
          this.addTable(
            scan,
            JSON.stringify(parsed, null, 2),
            extractHeaders(jsonContent, "json"),
            {
              fileExtension: "json",
              detectionPattern: "json_array",
              estimatedSize: `${parsed.length} rows`,
            },
          );
        }
      } catch {
        // Invalid JSON, skip
        scan.skippedCount++;
      }
    }
  }

  /**
   * Detect tab-separated tables (TSV format). Must have at least 2 columns
   * and 2 rows (header + 1 data row), with the same column count on every row.
   */
  private scanTsvTables(content: string, scan: TableScan): void {
    const tsvRegex =
      /^[^\t\n]+\t[^\t\n]+(?:\t[^\t\n]+)*\n(?:[^\t\n]+\t[^\t\n]+(?:\t[^\t\n]+)*\n?)+/gm;
    let match;

    while ((match = tsvRegex.exec(content)) !== null) {
      const tsvContent = match[0].trim();
      const rows = tsvContent.split("\n").filter((row) => row.trim().length > 0);

      if (rows.length < 2) continue;

      const columnCounts = rows.map((row) => row.split("\t").length);
      const firstColumnCount = columnCounts[0];
      const isConsistent = columnCounts.every((count) => count === firstColumnCount);

      if (!isConsistent || firstColumnCount < 2) continue;

      scan.foundAny = true;
      this.addTable(scan, tsvContent, extractHeaders(tsvContent, "tsv"), {
        fileExtension: "tsv",
        detectionPattern: "tsv_table",
        estimatedSize: describeRowCount(rows.length - 1),
      });
    }
  }

  /** Detect pipe-delimited markdown tables with any number of columns (minimum 2) */
  private scanMarkdownTables(content: string, scan: TableScan): void {
    const csvRegex =
      /^[^|\n]*\|[^|\n]+(?:\|[^|\n]*)+\n\s*\|?[-:\s]+\|[-|:\s]+(?:\|[-|:\s]+)*\|?\s*\n(?:[^|\n]*\|[^|\n]+(?:\|[^|\n]*)+\n?)+/gm;
    let match;

    while ((match = csvRegex.exec(content)) !== null) {
      scan.foundAny = true;
      const csvContent = match[0].trim();
      const rows = csvContent.split("\n").filter((row) => !row.match(MARKDOWN_SEPARATOR_ROW));

      // Require at least header + 1 data row (rows.length >= 2)
      if (rows.length < 2) {
        // Skip tables with insufficient data
        scan.skippedCount++;
        continue;
      }

      this.addTable(scan, csvContent, extractHeaders(csvContent, "markdown"), {
        fileExtension: "csv",
        detectionPattern: "markdown_table",
        estimatedSize: describeRowCount(rows.length - 1),
      });
    }
  }
}
